'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Shield,
  User,
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { emailSchema, passwordSchema } from '@/lib/form-validators';
import { TurnstileWidget, TURNSTILE_ENABLED } from '@/components/auth/turnstile-widget';
import {
  AuthInput,
  AuthButton,
  AuthDivider,
  OAuthButtons,
  OTPInput,
  PasswordStrength,
  AuthLayout,
} from '@/components/auth/auth-layout';

// ─── Validation Schemas ──────────────────────────────────────────────────────

const credentialsSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
});

type FieldErrors = Record<string, string>;

type Step = 'credentials' | 'profile' | 'verify';

const STEPS: { key: Step; icon: typeof Mail; label: string }[] = [
  { key: 'credentials', icon: Mail, label: 'Account' },
  { key: 'profile', icon: User, label: 'Profile' },
  { key: 'verify', icon: Shield, label: 'Verify' },
];

const stepVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

function StepIndicator({ current, steps }: { current: Step; steps: typeof STEPS }) {
  const currentIndex = steps.findIndex((s) => s.key === current);

  return (
    <div className="mb-8 flex items-center justify-center">
      {steps.map((step, i) => {
        const isCompleted = i < currentIndex;
        const isCurrent = i === currentIndex;
        const Icon = step.icon;

        return (
          <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center">
              <motion.div
                animate={{
                  backgroundColor:
                    isCompleted || isCurrent ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.06)',
                  scale: isCurrent ? 1.1 : 1,
                }}
                transition={{ duration: 0.2 }}
                className="flex h-11 w-11 items-center justify-center rounded-full"
              >
                {isCompleted ? (
                  <Check className="h-5 w-5 text-zinc-950" />
                ) : (
                  <Icon className={`h-5 w-5 ${isCurrent ? 'text-zinc-950' : 'text-white/30'}`} />
                )}
              </motion.div>
              <span
                className={`mt-1.5 text-xs font-medium ${
                  isCurrent ? 'text-white/70' : 'text-white/25'
                }`}
              >
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <motion.div
                animate={{
                  backgroundColor: isCompleted ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.06)',
                }}
                transition={{ duration: 0.2 }}
                className="mx-4 -mt-4 h-px w-12"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SignupForm() {
  const { signUp, verifyOtp, resendVerification } = useAuth();
  const searchParams = useSearchParams();

  const isInviteFlow = searchParams.get('invite') === 'true';
  const urlInviteEmail = isInviteFlow ? searchParams.get('email') : null;
  const [isInviteLocked, setIsInviteLocked] = useState(!!urlInviteEmail);

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState(urlInviteEmail ?? '');

  useEffect(() => {
    if (!isInviteFlow || urlInviteEmail) return;
    try {
      const stored = sessionStorage.getItem('virza-invite-email');
      if (stored) {
        setEmail(stored);
        setIsInviteLocked(true);
      }
    } catch {
      // Storage unavailable
    }
  }, [isInviteFlow, urlInviteEmail]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileResetRef = useRef<(() => void) | null>(null);
  const [resendTurnstileToken, setResendTurnstileToken] = useState<string | null>(null);
  const resendTurnstileResetRef = useRef<(() => void) | null>(null);

  // OAuth redirect state — disables the credentials form when provider redirect is in progress
  const [oauthRedirecting, setOauthRedirecting] = useState(false);

  // OTP state
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const [otpError, setOtpError] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const verifyingRef = useRef(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Client-side rate limiting for OTP verification
  const [_failedAttempts, setFailedAttempts] = useState(0);
  const failedAttemptsRef = useRef(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockCountdown, setLockCountdown] = useState(0);

  const isLocked = lockedUntil !== null || lockCountdown > 0;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((p) => p - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, lockedUntil - Date.now());
      setLockCountdown(Math.ceil(remaining / 1000));
      if (remaining <= 0) {
        setLockedUntil(null);
        setLockCountdown(0);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const handleCredentials = (e: React.FormEvent) => {
    e.preventDefault();
    if (oauthRedirecting) return;
    setFieldErrors({});
    const result = credentialsSchema.safeParse({ email, password, confirmPassword });
    if (!result.success) {
      const errors: FieldErrors = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        if (!errors[key]) errors[key] = issue.message;
      });
      setFieldErrors(errors);
      return;
    }
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error('Please complete the security check');
      return;
    }
    setStep('profile');
  };

  const handleProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    const result = profileSchema.safeParse({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });
    if (!result.success) {
      const errors: FieldErrors = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        if (!errors[key]) errors[key] = issue.message;
      });
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password, firstName.trim(), lastName.trim(), turnstileToken ?? undefined);
      setStep('verify');
      setResendCooldown(30);
    } catch (error) {
      const { friendlyErrorMessage } = await import('@/lib/toast-error');
      toast.error(friendlyErrorMessage(error));
      turnstileResetRef.current?.();
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = useCallback(
    async (code: string) => {
      if (code.length !== 6 || !email || isLocked || verifyingRef.current) return;

      verifyingRef.current = true;
      setVerifying(true);
      setOtpError(false);
      setOtpErrorMessage(null);
      try {
        await verifyOtp(email, code);
        failedAttemptsRef.current = 0;
        setFailedAttempts(0);
        toast.success('Email verified');
      } catch (error) {
        setOtp(new Array(6).fill(''));

        const isRateLimited =
          (error instanceof Error &&
            /rate.limit|too.many.request|forbidden/i.test(error.message)) ||
          (error &&
            typeof error === 'object' &&
            'status' in error &&
            (error as { status: number }).status === 403);

        if (isRateLimited) {
          setOtpError(true);
          setOtpErrorMessage('Too many requests. Please wait a few minutes and try again.');
          setLockedUntil(Date.now() + 120_000);
          failedAttemptsRef.current = 0;
          setFailedAttempts(0);
          toast.error(
            "You've been temporarily rate-limited. Please wait a few minutes before trying again.",
          );
        } else {
          setOtpError(true);
          setOtpErrorMessage('Invalid code. Please try again.');
          const next = failedAttemptsRef.current + 1;
          failedAttemptsRef.current = next;
          setFailedAttempts(next);
          if (next >= 5) {
            setLockedUntil(Date.now() + 60_000);
            failedAttemptsRef.current = 0;
            setFailedAttempts(0);
            toast.error('Too many failed attempts. Please wait 60 seconds.');
          } else {
            const { friendlyErrorMessage } = await import('@/lib/toast-error');
            toast.error(friendlyErrorMessage(error));
          }
        }
      } finally {
        verifyingRef.current = false;
        setVerifying(false);
      }
    },
    [email, verifyOtp, isLocked],
  );

  useEffect(() => {
    const code = otp.join('');
    if (code.length === 6) {
      handleVerify(code);
    }
  }, [otp, handleVerify]);

  const handleResend = async () => {
    if (resendCooldown > 0 || !email) return;
    if (TURNSTILE_ENABLED && !resendTurnstileToken) {
      toast.error('Please complete the security check before resending');
      return;
    }
    try {
      await resendVerification(email, resendTurnstileToken ?? undefined);
      toast.success('Code resent');
      setResendCooldown(60);
      resendTurnstileResetRef.current?.();
      setResendTurnstileToken(null);
    } catch (error) {
      const { friendlyErrorMessage: fmsg } = await import('@/lib/toast-error');
      toast.error(fmsg(error));
      resendTurnstileResetRef.current?.();
      setResendTurnstileToken(null);
    }
  };

  return (
    <AuthLayout>
      <StepIndicator current={step} steps={STEPS} />

      <AnimatePresence mode="wait">
        {step === 'credentials' && (
          <motion.div
            key="credentials"
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <div className="mb-6 text-center">
              <h1 className="mb-2 text-2xl font-semibold tracking-[-0.02em] text-white">
                Create an account
              </h1>
              <p className="text-[15px] text-white/40">Start organizing your research.</p>
            </div>

            <div className="mb-6 space-y-4">
              <OAuthButtons
                onOAuthStart={() => setOauthRedirecting(true)}
                onOAuthReset={() => setOauthRedirecting(false)}
              />
              <AuthDivider />
            </div>

            <form onSubmit={handleCredentials} className="space-y-4">
              <AuthInput
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  if (isInviteLocked) return;
                  setEmail(e.target.value);
                  setFieldErrors((p: FieldErrors) => {
                    const next = { ...p };
                    delete next.email;
                    return next;
                  });
                }}
                error={fieldErrors.email}
                required
                readOnly={isInviteLocked}
                autoComplete="email"
                autoFocus={!isInviteLocked}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                className={isInviteLocked ? 'cursor-not-allowed opacity-60' : ''}
              />
              {isInviteLocked && (
                <p className="-mt-2 text-xs text-white/30">
                  This email matches your invitation and can&apos;t be changed.
                </p>
              )}

              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-white/50">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFieldErrors((p: FieldErrors) => {
                        const next = { ...p };
                        delete next.password;
                        return next;
                      });
                    }}
                    required
                    autoComplete="new-password"
                    minLength={8}
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                    className={`h-11 w-full rounded-lg border bg-white/[0.05] px-3.5 pr-11 text-[15px] text-white transition-all duration-150 placeholder:text-white/25 focus:border-white/20 focus:bg-white/[0.07] focus:ring-2 focus:ring-white/[0.08] focus:outline-none ${fieldErrors.password ? 'border-red-500/50 focus:ring-red-500/20' : 'border-white/[0.08]'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute top-1/2 right-3 -translate-y-1/2 text-white/25 transition-colors hover:text-white/50"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p id="password-error" className="mt-1.5 text-[13px] text-red-400" role="alert">
                    {fieldErrors.password}
                  </p>
                )}
                {password && !fieldErrors.password && (
                  <div className="mt-2">
                    <PasswordStrength password={password} />
                  </div>
                )}
              </div>

              <AuthInput
                label="Confirm password"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFieldErrors((p: FieldErrors) => {
                    const next = { ...p };
                    delete next.confirmPassword;
                    return next;
                  });
                }}
                error={fieldErrors.confirmPassword}
                required
                autoComplete="new-password"
                minLength={8}
                aria-invalid={!!fieldErrors.confirmPassword}
                aria-describedby={fieldErrors.confirmPassword ? 'confirm-error' : undefined}
              />

              <TurnstileWidget
                onVerify={setTurnstileToken}
                onError={() => setTurnstileToken(null)}
                onExpire={() => setTurnstileToken(null)}
                resetRef={turnstileResetRef}
              />

              <div className="pt-2">
                <AuthButton
                  type="submit"
                  disabled={oauthRedirecting || (TURNSTILE_ENABLED && !turnstileToken)}
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </AuthButton>
              </div>
            </form>

            <p className="mt-8 text-center text-sm text-white/30">
              Already have an account?{' '}
              <Link href="/login" className="font-medium text-white/60 hover:text-white">
                Sign in
              </Link>
            </p>
          </motion.div>
        )}

        {step === 'profile' && (
          <motion.div
            key="profile"
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={() => setStep('credentials')}
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/40 transition-colors hover:text-white/70"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <div className="mb-6 text-center">
              <h1 className="mb-2 text-2xl font-semibold tracking-[-0.02em] text-white">
                Your profile
              </h1>
              <p className="text-[15px] text-white/40">This helps personalize your experience.</p>
            </div>

            <form onSubmit={handleProfile} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <AuthInput
                  label="First name"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setFieldErrors((p: FieldErrors) => {
                      const next = { ...p };
                      delete next.firstName;
                      return next;
                    });
                  }}
                  error={fieldErrors.firstName}
                  required
                  autoFocus
                  aria-invalid={!!fieldErrors.firstName}
                />
                <AuthInput
                  label="Last name"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setFieldErrors((p: FieldErrors) => {
                      const next = { ...p };
                      delete next.lastName;
                      return next;
                    });
                  }}
                  error={fieldErrors.lastName}
                  required
                  aria-invalid={!!fieldErrors.lastName}
                />
              </div>

              <div className="rounded-lg bg-white/[0.04] px-4 py-3">
                <p className="text-xs text-white/30">Signing up as</p>
                <p className="text-sm font-medium text-white/70">{email}</p>
              </div>

              <div className="pt-2">
                <AuthButton type="submit" loading={loading}>
                  {loading ? (
                    'Creating account…'
                  ) : (
                    <>
                      Send verification code <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </AuthButton>
              </div>
            </form>
          </motion.div>
        )}

        {step === 'verify' && (
          <motion.div
            key="verify"
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={() => setStep('profile')}
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/40 transition-colors hover:text-white/70"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <div className="mb-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.1 }}
                className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.06]"
              >
                <Mail className="h-7 w-7 text-white/60" />
              </motion.div>
              <h1 className="mb-2 text-2xl font-semibold tracking-[-0.02em] text-white">
                Check your email
              </h1>
              <p className="text-[15px] text-white/40">
                We sent a 6-digit code to <span className="font-medium text-white/70">{email}</span>
              </p>
            </div>

            <div className="space-y-6">
              <OTPInput
                value={otp}
                onChange={setOtp}
                error={otpError}
                disabled={verifying || isLocked}
                showSeparator
              />

              {isLocked && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-sm text-red-400"
                  role="alert"
                >
                  Too many failed attempts. Try again in {lockCountdown}s.
                </motion.p>
              )}

              {otpError && !isLocked && otpErrorMessage && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-sm text-red-400"
                >
                  {otpErrorMessage}
                </motion.p>
              )}

              {verifying && (
                <div className="flex items-center justify-center gap-2 text-sm text-white/40">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying…
                </div>
              )}

              <TurnstileWidget
                onVerify={setResendTurnstileToken}
                onError={() => setResendTurnstileToken(null)}
                onExpire={() => setResendTurnstileToken(null)}
                resetRef={resendTurnstileResetRef}
              />

              <div className="text-center">
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || (TURNSTILE_ENABLED && !resendTurnstileToken)}
                  className={`text-sm ${resendCooldown > 0 || (TURNSTILE_ENABLED && !resendTurnstileToken) ? 'cursor-not-allowed text-white/20' : 'font-medium text-white/50 hover:text-white/80'}`}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
